import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

let refreshTokenPromise = null;

const getStoredAccessToken = () => {
	const token = localStorage.getItem("token");
	if (!token) return "";

	try {
		const parsedToken = JSON.parse(token);
		return typeof parsedToken === "string" ? parsedToken : "";
	} catch (error) {
		return typeof token === "string" ? token : "";
	}
};

const decodeJwtPayload = token => {
	if (!token || typeof token !== "string") {
		return null;
	}

	try {
		const payloadSegment = token.split(".")[1];
		if (!payloadSegment) {
			return null;
		}

		const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
		const normalizedBase64 = base64.padEnd(
			base64.length + ((4 - (base64.length % 4)) % 4),
			"="
		);
		return JSON.parse(window.atob(normalizedBase64));
	} catch (error) {
		return null;
	}
};

const getTokenUserId = token => {
	const payload = decodeJwtPayload(token);
	if (!payload?.id) {
		return null;
	}

	return String(payload.id);
};

const isJwtTokenExpired = token => {
	const payload = decodeJwtPayload(token);
	const expirationTime = Number(payload?.exp);

	if (!Number.isFinite(expirationTime) || expirationTime <= 0) {
		return true;
	}

	const expirationMs = expirationTime * 1000;
	const clockSkewMs = 5000;
	return Date.now() >= expirationMs - clockSkewMs;
};

const canUseStoredAccessToken = token =>
	!!token && !isJwtTokenExpired(token);

const fetchUserFromAccessToken = async token => {
	const userId = getTokenUserId(token);
	if (!userId) {
		return null;
	}

	try {
		const { data } = await api.get(`/users/${userId}`);
		return data || null;
	} catch (error) {
		return null;
	}
};

const isSessionExpiredResponse = error => {
	const status = error?.response?.status;
	const errorCode = error?.response?.data?.error;
	const requestUrl = `${error?.config?.url || ""}`;

	if (status !== 401) {
		return false;
	}

	return errorCode === "ERR_SESSION_EXPIRED" || requestUrl.includes("/auth/refresh_token");
};

const isRefreshTokenRequest = requestUrl =>
	`${requestUrl || ""}`.includes("/auth/refresh_token");

const shouldSkipRefreshAttempt = requestUrl => {
	const normalizedUrl = `${requestUrl || ""}`;

	return (
		normalizedUrl.includes("/auth/login") ||
		normalizedUrl.includes("/auth/signup") ||
		normalizedUrl.includes("/auth/refresh_token")
	);
};

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState({});

	useEffect(() => {
		const requestInterceptor = api.interceptors.request.use(
			config => {
				const token = getStoredAccessToken();
				if (token) {
					config.headers["Authorization"] = `Bearer ${token}`;
				}
				return config;
			},
			error => Promise.reject(error)
		);

		const responseInterceptor = api.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config;
				const status = error?.response?.status;
				const shouldTryRefresh =
					originalRequest &&
					!originalRequest._retry &&
					!shouldSkipRefreshAttempt(originalRequest?.url) &&
					(status === 401 || status === 403 || isSessionExpiredResponse(error));

				if (shouldTryRefresh) {
					originalRequest._retry = true;

					try {
						if (!refreshTokenPromise) {
							refreshTokenPromise = api
								.post("/auth/refresh_token")
								.then(({ data }) => {
									if (data?.token) {
										localStorage.setItem("token", JSON.stringify(data.token));
										api.defaults.headers.Authorization = `Bearer ${data.token}`;
										setIsAuth(true);
									}
									return data;
								})
								.finally(() => {
									refreshTokenPromise = null;
								});
						}

						await refreshTokenPromise;
						return api(originalRequest);
					} catch (refreshError) {
						const currentToken = getStoredAccessToken();
						if (canUseStoredAccessToken(currentToken)) {
							api.defaults.headers.Authorization = `Bearer ${currentToken}`;
							const fallbackUser = await fetchUserFromAccessToken(currentToken);
							if (fallbackUser) {
								setUser(fallbackUser);
								setIsAuth(true);
								return Promise.reject(refreshError);
							}
						}

						if (
							isSessionExpiredResponse(refreshError) ||
							refreshError?.response?.status === 401
						) {
							localStorage.removeItem("token");
							api.defaults.headers.Authorization = undefined;
							setIsAuth(false);
							setUser({});
						}
						return Promise.reject(refreshError);
					}
				}

				if (
					isSessionExpiredResponse(error) &&
					!isRefreshTokenRequest(error?.config?.url)
				) {
					localStorage.removeItem("token");
					api.defaults.headers.Authorization = undefined;
					setIsAuth(false);
					setUser({});
				}

				return Promise.reject(error);
			}
		);

		return () => {
			api.interceptors.request.eject(requestInterceptor);
			api.interceptors.response.eject(responseInterceptor);
		};
	}, []);

	useEffect(() => {
		(async () => {
			const token = getStoredAccessToken();

			if (token) {
				api.defaults.headers.Authorization = `Bearer ${token}`;
			}

				try {
					const { data } = await api.post("/auth/refresh_token");

				if (data?.token) {
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}

					setIsAuth(true);
					setUser(data?.user || {});
				} catch (err) {
					const status = err?.response?.status;

					if (canUseStoredAccessToken(token)) {
						api.defaults.headers.Authorization = `Bearer ${token}`;
						const fallbackUser = await fetchUserFromAccessToken(token);

						if (fallbackUser) {
							setUser(fallbackUser);
							setIsAuth(true);
						} else if (status !== 401 && status !== 403) {
							// Keep the user logged in when refresh fails due transient issues.
							setIsAuth(true);
						} else {
							localStorage.removeItem("token");
							api.defaults.headers.Authorization = undefined;
							setIsAuth(false);
							setUser({});
						}
					} else if (
						isSessionExpiredResponse(err) ||
						status === 401 ||
						status === 403
					) {
						localStorage.removeItem("token");
						api.defaults.headers.Authorization = undefined;
						setIsAuth(false);
					setUser({});
				}
			}

			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!isAuth) {
			return undefined;
		}

		const interval = setInterval(async () => {
				try {
					const { data } = await api.post("/auth/refresh_token");
				if (data?.token) {
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}
					if (data?.user) {
						setUser(data.user);
					}
				} catch (err) {
					const status = err?.response?.status;
					const currentToken = getStoredAccessToken();

					if (
						(isSessionExpiredResponse(err) || status === 401 || status === 403) &&
						canUseStoredAccessToken(currentToken)
					) {
						api.defaults.headers.Authorization = `Bearer ${currentToken}`;
						const fallbackUser = await fetchUserFromAccessToken(currentToken);
						if (fallbackUser) {
							setUser(fallbackUser);
						}
						setIsAuth(true);
						return;
					}

					if (isSessionExpiredResponse(err) || status === 401 || status === 403) {
						localStorage.removeItem("token");
						api.defaults.headers.Authorization = undefined;
						setIsAuth(false);
					setUser({});
				}
			}
		}, 15 * 60 * 1000);

		return () => clearInterval(interval);
	}, [isAuth]);

	useEffect(() => {
		if (!user?.id) {
			return undefined;
		}

		const socket = openSocket();

		socket.on("user", data => {
			if (data.action === "update" && data.user.id === user.id) {
				setUser(data.user);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user?.id]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const updateCurrentUser = nextUser => {
		if (!nextUser || typeof nextUser !== "object") {
			return;
		}

		setUser(prevUser => ({
			...prevUser,
			...nextUser
		}));
	};

	return {
		isAuth,
		user,
		loading,
		handleLogin,
		handleLogout,
		updateCurrentUser
	};
};

export default useAuth;
